import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { useStylePresets } from "@/hooks/use-style-presets";

interface StylePresetsProps {
  value: Record<string, unknown> | null;
  onApply: (styleData: Record<string, unknown>) => void;
}

function hasPersistedStyles(value: Record<string, unknown> | null): boolean {
  return !!value && Object.keys(value).length > 0;
}

export default function StylePresets({ value, onApply }: StylePresetsProps) {
  const { presets, isLoading, isSaving, error, savePreset, removePreset } = useStylePresets();
  const [draftName, setDraftName] = useState("");

  const grouped = useMemo(
    () => ({
      global: presets.filter((preset) => preset.source === "global"),
      tenant: presets.filter((preset) => preset.source === "tenant"),
    }),
    [presets],
  );

  const canSave = hasPersistedStyles(value) && draftName.trim().length > 0 && !isSaving;

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-[11px] font-semibold text-[var(--color-foreground)]">Save current style</div>
            <div className="text-[10px] text-[var(--color-muted-foreground)]">
              Presets store the canonical `_dssStyles` payload for reuse across blocks.
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            aria-label="Style preset name"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            placeholder="Blue Card Style"
            className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[12px]"
          />
          <button
            type="button"
            disabled={!canSave}
            onClick={async () => {
              if (!value || !canSave) return;
              await savePreset({
                name: draftName.trim(),
                styleData: value,
              });
              setDraftName("");
            }}
            className="rounded-md border border-[var(--color-primary)] px-3 py-2 text-[11px] font-medium text-[var(--color-primary)] disabled:cursor-not-allowed disabled:border-[var(--color-border)] disabled:text-[var(--color-muted-foreground)]"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
        {!hasPersistedStyles(value) ? (
          <div className="mt-2 text-[10px] text-[var(--color-muted-foreground)]">
            Add some styles to the selected block before saving a preset.
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[10px] text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <PresetGroup
          title="Global presets"
          emptyLabel={isLoading ? "Loading presets..." : "No global presets yet."}
          presets={grouped.global}
          onApply={onApply}
        />
        <PresetGroup
          title="Tenant presets"
          emptyLabel={isLoading ? "Loading presets..." : "No tenant presets yet."}
          presets={grouped.tenant}
          onApply={onApply}
          onDelete={removePreset}
        />
      </div>
    </div>
  );
}

interface PresetGroupProps {
  title: string;
  emptyLabel: string;
  presets: Array<{
    id: string;
    name: string;
    category: string;
    styleData: Record<string, unknown>;
    source: "tenant" | "global";
  }>;
  onApply: (styleData: Record<string, unknown>) => void;
  onDelete?: (id: string) => Promise<void>;
}

function PresetGroup({ title, emptyLabel, presets, onApply, onDelete }: PresetGroupProps) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="mb-2 text-[11px] font-semibold text-[var(--color-foreground)]">{title}</div>
      {presets.length === 0 ? (
        <div className="text-[10px] text-[var(--color-muted-foreground)]">{emptyLabel}</div>
      ) : (
        <div className="flex flex-col gap-2">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-[11px] font-medium text-[var(--color-foreground)]">{preset.name}</div>
                <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                  {preset.category}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onApply(preset.styleData)}
                  className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[10px] font-medium text-[var(--color-foreground)]"
                >
                  Apply
                </button>
                {onDelete ? (
                  <button
                    type="button"
                    aria-label={`Delete preset ${preset.name}`}
                    onClick={async () => {
                      await onDelete(preset.id);
                    }}
                    className="rounded-md border border-[var(--color-border)] p-1 text-[var(--color-muted-foreground)]"
                  >
                    <Trash2 size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
